import type { ApplicationListResponse } from '@acme/api';
import { ApplicationService } from '@acme/api/client';
import { ApplicationCard } from '@acme/features';
import { PaginatedList, TabItem, Tabs } from '@acme/foundation';
import type { ApplicationListTabConfig } from '@entities/applicationList';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type ApplicationListProps = {
  tabs: ApplicationListTabConfig[];
};

const loadingFallback = (
  <div className="rounded-lg bg-white p-6 shadow text-center text-gray-500">
    Загрузка…
  </div>
);

type ResponseData = Awaited<
  ReturnType<typeof ApplicationService.getApiV1ApplicationList>
>;

function extract(response: ResponseData) {
  if (response?.isSuccess === false) {
    throw new Error(response.errorText || 'Не удалось загрузить заявки');
  }
  const items = (response?.data?.items ?? []).slice();
  items.sort((a, b) => {
    const aVal = (a as ApplicationListResponse).createdAt ?? '';
    const bVal = (b as ApplicationListResponse).createdAt ?? '';
    return new Date(bVal).getTime() - new Date(aVal).getTime();
  });
  return {
    items,
    totalCount: response?.data?.totalCount ?? 0,
  };
}

export function ApplicationList({ tabs }: ApplicationListProps) {
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState(tabs[0]?.key ?? 'all');

  const handleTabChange = useCallback((key: string) => {
    setActiveKey(key);
  }, []);

  const tabItems: TabItem[] = useMemo(
    () =>
      tabs.map((tab) => ({
        key: tab.key,
        title: tab.title,
        content:
          activeKey === tab.key ? (
            <PaginatedList
              key={tab.key}
              method={ApplicationService.getApiV1ApplicationList}
              params={{ epochId: tab.epochId ?? undefined }}
              extract={extract}
              pageSize={20}
              loadingFallback={loadingFallback}
            >
              {(items) => (
                <div className="grid grid-cols-3 gap-4">
                  {(items as ApplicationListResponse[]).map((card) => (
                    <ApplicationCard
                      key={card.id}
                      application={card}
                      amount={card.fiat?.amount}
                      currency={card.fiat?.code ?? ''}
                      onClick={() =>
                        card.id && navigate(`/application/${card.id}`)
                      }
                    />
                  ))}
                </div>
              )}
            </PaginatedList>
          ) : (
            <div className="min-h-[200px]" />
          ),
      })),
    [tabs, activeKey, navigate]
  );

  return (
    <Tabs
      items={tabItems}
      defaultActiveKey={activeKey}
      onTabChange={handleTabChange}
    />
  );
}
